// B2B Invoice Management
import { useState, useCallback, useMemo } from "react";
import {
  FileText,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  Send,
  Eye,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import { ExportButton } from "@/components/admin/shared/ExportButton";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useB2BInvoices } from "@/hooks/admin/useB2B";
import type { B2BInvoice, B2BInvoiceStatus } from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<B2BInvoiceStatus, string> = {
  draft: "Brouillon",
  sent: "Envoyée",
  overdue: "En retard",
  paid: "Payée",
  cancelled: "Annulée",
};

const STATUS_COLORS: Record<B2BInvoiceStatus, string> = {
  draft: "bg-stone-100 text-stone-600",
  sent: "bg-blue-100 text-blue-700",
  overdue: "bg-red-100 text-red-700",
  paid: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-stone-100 text-stone-400",
};

const STATUS_ICONS: Record<B2BInvoiceStatus, typeof Clock> = {
  draft: FileText,
  sent: Send,
  overdue: AlertCircle,
  paid: CheckCircle2,
  cancelled: XCircle,
};

const EXPORT_COLUMNS = [
  { key: "invoice_number", label: "N° Facture" },
  { key: "company_name", label: "Client" },
  { key: "amount_fcfa", label: "Montant (FCFA)" },
  { key: "status", label: "Statut" },
  { key: "issued_at", label: "Émis le" },
  { key: "due_at", label: "Dû le" },
  { key: "paid_at", label: "Payé le" },
];

// ---------------------------------------------------------------------------
// Invoice detail modal
// ---------------------------------------------------------------------------

interface DetailModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly invoice: B2BInvoice | null;
}

function InvoiceDetailModal({ open, onClose, invoice }: DetailModalProps) {
  if (!invoice) return null;

  const daysUntilDue = Math.ceil(
    (new Date(invoice.due_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );

  const rows: readonly { label: string; value: string; highlight?: boolean }[] =
    [
      { label: "N Facture", value: invoice.invoice_number },
      { label: "Client", value: invoice.company_name },
      {
        label: "Montant",
        value: `${invoice.amount_fcfa.toLocaleString("fr-SN")} FCFA`,
      },
      { label: "Statut", value: STATUS_LABELS[invoice.status] },
      {
        label: "Emis le",
        value: new Date(invoice.issued_at).toLocaleDateString("fr-SN"),
      },
      {
        label: "Échéance",
        value: new Date(invoice.due_at).toLocaleDateString("fr-SN"),
        highlight: invoice.status === "overdue",
      },
      {
        label: "Délai restant",
        value:
          invoice.status === "paid"
            ? "Payée"
            : daysUntilDue > 0
              ? `${daysUntilDue} jour(s)`
              : `${Math.abs(daysUntilDue)} jour(s) de retard`,
        highlight: daysUntilDue < 0,
      },
      {
        label: "Payé le",
        value: invoice.paid_at
          ? new Date(invoice.paid_at).toLocaleDateString("fr-SN")
          : "---",
      },
    ];

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={`Facture ${invoice.invoice_number}`}
      size="md"
      footer={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => {
              /* PDF export placeholder */
            }}
          >
            <Download className="h-4 w-4" />
            Telecharger PDF
          </Button>
        </div>
      }
    >
      <dl className="space-y-3">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-start justify-between gap-4 border-b border-stone-100 pb-2 last:border-0"
          >
            <dt className="text-sm font-medium text-stone-500">{r.label}</dt>
            <dd
              className={`text-sm text-right ${
                r.highlight ? "font-medium text-red-600" : "text-stone-900"
              }`}
            >
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function B2BInvoices() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string | null>>({
    status: null,
    date_from: null,
    date_to: null,
  });
  const [viewingInvoice, setViewingInvoice] = useState<B2BInvoice | null>(null);

  const params = useMemo(
    () => ({
      status: (filters.status as B2BInvoiceStatus) ?? undefined,
      date_from: filters.date_from ?? undefined,
      date_to: filters.date_to ?? undefined,
    }),
    [filters],
  );

  const { data, isLoading, isError, refetch } = useB2BInvoices(params);

  const invoices = data?.invoices ?? [];

  // Client-side search
  const filteredInvoices = useMemo(() => {
    if (!search) return invoices;
    const q = search.toLowerCase();
    return invoices.filter(
      (i) =>
        i.company_name.toLowerCase().includes(q) ||
        i.invoice_number.toLowerCase().includes(q),
    );
  }, [invoices, search]);

  const kpiData = useMemo(() => {
    const totalAmount = invoices.reduce((s, i) => s + i.amount_fcfa, 0);
    const paid = invoices.filter((i) => i.status === "paid");
    const paidAmount = paid.reduce((s, i) => s + i.amount_fcfa, 0);
    const overdue = invoices.filter((i) => i.status === "overdue");
    const overdueAmount = overdue.reduce((s, i) => s + i.amount_fcfa, 0);
    const collectionRate =
      totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
    return {
      totalAmount,
      paidCount: paid.length,
      overdueCount: overdue.length,
      overdueAmount,
      collectionRate,
    };
  }, [invoices]);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
  }, []);

  const filterConfigs = useMemo(
    () => [
      {
        key: "status",
        label: "Statut",
        type: "select" as const,
        options: [
          { value: "draft", label: "Brouillon" },
          { value: "sent", label: "Envoyée" },
          { value: "overdue", label: "En retard" },
          { value: "paid", label: "Payée" },
          { value: "cancelled", label: "Annulée" },
        ],
      },
      { key: "date_from", label: "Du", type: "date" as const },
      { key: "date_to", label: "Au", type: "date" as const },
    ],
    [],
  );

  const exportData = useMemo(
    () =>
      filteredInvoices.map((i) => ({
        invoice_number: i.invoice_number,
        company_name: i.company_name,
        amount_fcfa: i.amount_fcfa,
        status: STATUS_LABELS[i.status],
        issued_at: new Date(i.issued_at).toLocaleDateString("fr-SN"),
        due_at: new Date(i.due_at).toLocaleDateString("fr-SN"),
        paid_at: i.paid_at
          ? new Date(i.paid_at).toLocaleDateString("fr-SN")
          : "",
      })) as Record<string, unknown>[],
    [filteredInvoices],
  );

  const columns: readonly ColumnDef<B2BInvoice>[] = useMemo(
    () => [
      {
        id: "invoice_number",
        header: "N° Facture",
        cell: (i) => (
          <span className="font-mono text-sm font-medium text-stone-900">
            {i.invoice_number}
          </span>
        ),
      },
      {
        id: "company_name",
        header: "Client",
        cell: (i) => (
          <span className="font-medium text-stone-900">{i.company_name}</span>
        ),
      },
      {
        id: "amount_fcfa",
        header: "Montant",
        cell: (i) => (
          <span className="text-sm font-medium text-stone-900">
            {i.amount_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (i) => {
          const Icon = STATUS_ICONS[i.status];
          return (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[i.status]}`}
            >
              <Icon className="h-3 w-3" />
              {STATUS_LABELS[i.status]}
            </span>
          );
        },
      },
      {
        id: "issued_at",
        header: "Emis le",
        cell: (i) => (
          <span className="text-sm text-stone-500">
            {new Date(i.issued_at).toLocaleDateString("fr-SN")}
          </span>
        ),
      },
      {
        id: "due_at",
        header: "Échéance",
        cell: (i) => {
          const isOverdue = i.status === "overdue";
          const daysUntilDue = Math.ceil(
            (new Date(i.due_at).getTime() - Date.now()) /
              (1000 * 60 * 60 * 24),
          );
          return (
            <div>
              <span
                className={`text-sm ${
                  isOverdue ? "font-medium text-red-600" : "text-stone-500"
                }`}
              >
                {new Date(i.due_at).toLocaleDateString("fr-SN")}
              </span>
              {i.status !== "paid" &&
                i.status !== "cancelled" &&
                daysUntilDue <= 7 && (
                  <p
                    className={`mt-0.5 text-xs ${
                      daysUntilDue < 0 ? "text-red-500" : "text-amber-500"
                    }`}
                  >
                    {daysUntilDue < 0
                      ? `${Math.abs(daysUntilDue)}j de retard`
                      : daysUntilDue === 0
                        ? "Échéance aujourd'hui"
                        : `${daysUntilDue}j restant(s)`}
                  </p>
                )}
            </div>
          );
        },
      },
      {
        id: "paid_at",
        header: "Payé le",
        cell: (i) => (
          <span className="text-sm text-stone-500">
            {i.paid_at
              ? new Date(i.paid_at).toLocaleDateString("fr-SN")
              : "---"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (i) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setViewingInvoice(i)}
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Voir details"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Telecharger PDF"
              onClick={() => {
                /* PDF export placeholder */
              }}
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Factures B2B
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement..."
                  : `${invoices.length} facture(s)`}
              </p>
            </div>
          </div>
          <ExportButton
            data={exportData}
            columns={EXPORT_COLUMNS}
            filename="factures-b2b"
          />
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <KPICard
            label="Total facture"
            value={kpiData.totalAmount}
            format="currency"
            icon={<FileText className="h-4 w-4" />}
          />
          <KPICard
            label="Payees"
            value={kpiData.paidCount}
            format="number"
            icon={<CheckCircle2 className="h-4 w-4" />}
          />
          <KPICard
            label="En retard"
            value={kpiData.overdueCount}
            format="number"
            icon={<Clock className="h-4 w-4" />}
          />
          <KPICard
            label="Montant en retard"
            value={kpiData.overdueAmount}
            format="currency"
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Taux de recouvrement"
            value={kpiData.collectionRate}
            format="percent"
          />
        </div>

        {/* Overdue alert */}
        {kpiData.overdueCount > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{kpiData.overdueCount}</strong> facture(s) en retard
              pour un total de{" "}
              <strong>
                {kpiData.overdueAmount.toLocaleString("fr-SN")} FCFA
              </strong>
              .
            </span>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par client ou n facture..."
          />
          <FilterBar
            filters={filterConfigs}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les factures.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Reessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && filteredInvoices.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-6 w-6" />}
                title="Aucune facture"
                description={
                  search || filters.status
                    ? "Aucune facture ne correspond aux filtres."
                    : "Les factures B2B apparaitront ici."
                }
              />
            ) : (
              <AdminDataTable
                data={filteredInvoices}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      <InvoiceDetailModal
        open={viewingInvoice !== null}
        onClose={() => setViewingInvoice(null)}
        invoice={viewingInvoice}
      />
    </AdminLayout>
  );
}
